import { useEffect } from 'react';
import { Phone, Calendar, ArrowRight, GraduationCap, Scale, Globe, Award, Quote } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import FloatingCta from '@/components/FloatingCta';
import SchemaJsonLd from '@/components/SchemaJsonLd';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import type { Attorney } from '@/data/attorneys';
import { practiceAreaLabels } from '@/data/practiceAreas';
import { pageDates } from '@/data/pageDates';
import { applyPageMeta } from '@/lib/pageMeta';

const SITE = 'https://www.morganlegalny.com';

interface Props {
  attorney: Attorney;
}

function buildSchema(a: Attorney) {
  const canonical = `${SITE}${a.url}`;
  const dates = pageDates(a.url);
  const personId = `${canonical}#person`;
  const photoUrl = `${SITE}${a.photos[0].fallback}`;

  const knowsLanguage = a.credentials
    .filter((c) => c.type === 'language')
    .map((c) => c.value.split(' ')[0]);

  const alumniOf = a.credentials
    .filter((c) => c.type === 'education')
    .map((c) => ({ '@type': 'EducationalOrganization', name: c.value.split('—')[0].trim() }));

  return [
    {
      '@context': 'https://schema.org',
      '@type': 'Person',
      '@id': personId,
      name: a.name,
      givenName: a.shortName,
      jobTitle: a.title,
      worksFor: { '@id': `${SITE}/#organization` },
      url: canonical,
      image: photoUrl,
      description: a.metaDescription,
      knowsLanguage: knowsLanguage.length ? knowsLanguage : undefined,
      alumniOf: alumniOf.length ? alumniOf : undefined,
      sameAs: a.sameAs && a.sameAs.length ? a.sameAs : undefined,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE}/` },
        { '@type': 'ListItem', position: 2, name: 'Our Attorneys', item: `${SITE}/about-us/` },
        { '@type': 'ListItem', position: 3, name: a.name, item: canonical },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'ProfilePage',
      url: canonical,
      name: a.metaTitle,
      mainEntity: { '@id': personId },
      datePublished: dates.published,
      dateModified: dates.modified,
      speakable: {
        '@type': 'SpeakableSpecification',
        cssSelector: ['h1', '.lead', '[data-speakable]'],
      },
    },
  ];
}

function HeroBanner({ a }: { a: Attorney }) {
  const initials = a.shortName.slice(0, 1) + (a.name.split(' ').slice(-1)[0][0] ?? '');
  const primary = a.photos[0];
  return (
    <section className="relative bg-navy pt-28 lg:pt-32 pb-20 lg:pb-28 overflow-hidden">
      <div className="absolute inset-0 overflow-hidden pointer-events-none select-none">
        <span
          className="absolute bottom-0 -right-8 font-display text-[10rem] lg:text-[16rem] font-bold text-white leading-none whitespace-nowrap"
          style={{ opacity: 0.03 }}
        >
          {initials.toUpperCase()}
        </span>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <nav aria-label="Breadcrumb" className="mb-6 font-body text-xs text-white/50 tracking-wide">
          <a href="/" className="hover:text-gold transition-colors">Home</a>
          <span className="mx-2 text-gold/60">/</span>
          <a href="/about-us/" className="hover:text-gold transition-colors">Our Attorneys</a>
          <span className="mx-2 text-gold/60">/</span>
          <span className="text-white/80">{a.name}</span>
        </nav>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 lg:gap-12 items-center">
          <div className="md:col-span-7 order-2 md:order-1">
            <div className="section-label mb-5 text-gold">{a.heroEyebrow}</div>

            <h1 className="font-display text-white text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold leading-[1.05] mb-4">
              {a.shortName} <span className="text-gold italic">{a.name.replace(a.shortName, '').trim()}</span>
            </h1>

            <p className="font-body text-white/85 text-lg lg:text-xl mb-2 font-semibold">{a.title}</p>
            {a.titleSubline ? (
              <p className="font-body text-gold/85 text-sm lg:text-base mb-7 tracking-wide uppercase">{a.titleSubline}</p>
            ) : null}

            <p className="lead font-body text-white/80 text-base lg:text-lg leading-relaxed mb-8 max-w-2xl" data-speakable>
              {a.heroLead}
            </p>

            <div className="flex flex-col sm:flex-row gap-3">
              <a href="/appointment/" className="btn-gold">
                <Calendar className="w-4 h-4" />
                Schedule Consultation
              </a>
              <a
                href="tel:8885291315"
                className="inline-flex items-center gap-2 border border-gold/40 text-gold font-body font-semibold px-6 py-3 transition-all duration-200 hover:bg-gold hover:text-navy"
              >
                <Phone className="w-4 h-4" />
                888-529-1315
              </a>
            </div>
          </div>

          <div className="md:col-span-5 order-1 md:order-2 flex justify-center md:justify-end">
            <div className="relative">
              <div className="absolute -inset-3 border border-gold/30" aria-hidden />
              <div className="absolute -inset-6 border border-gold/15" aria-hidden />
              <picture>
                <source srcSet={primary.webp} type="image/webp" />
                <img
                  src={primary.fallback}
                  alt={primary.alt}
                  width="480"
                  height="480"
                  loading="eager"
                  className="relative w-56 h-56 sm:w-64 sm:h-64 lg:w-72 lg:h-72 object-cover bg-navy/40"
                />
              </picture>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function PhotoGallery({ a }: { a: Attorney }) {
  if (a.photos.length < 2) return null;
  return (
    <section className="bg-cream py-12 lg:py-16 border-b border-navy/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 lg:gap-5">
          {a.photos.map((p) => (
            <picture key={p.webp}>
              <source srcSet={p.webp} type="image/webp" />
              <img
                src={p.fallback}
                alt={p.alt}
                loading="lazy"
                className="w-full aspect-square object-cover bg-navy/5"
                width="320"
                height="320"
              />
            </picture>
          ))}
        </div>
      </div>
    </section>
  );
}

function BioSections({ a }: { a: Attorney }) {
  const { ref, visible } = useScrollReveal(0.1);
  return (
    <section ref={ref as React.RefObject<HTMLElement>} className="py-20 lg:py-28 bg-cream">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="space-y-12 lg:space-y-16">
          {a.sections.map((sec, i) => (
            <article
              key={sec.heading}
              className={`grid grid-cols-1 md:grid-cols-12 gap-6 lg:gap-10 transition-all duration-700 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'
              }`}
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              <header className="md:col-span-4">
                {/* Нумерация «Section 1…N» снята 03.09.2026 вместе с такой же в
                    ContentPageTemplate: читателю она ничего не сообщала, а на
                    карточке юриста читалась как недособранный шаблон. */}
                <h2 className="font-display text-navy text-2xl lg:text-3xl font-bold leading-tight">
                  {sec.heading}
                </h2>
              </header>
              <div className="md:col-span-8 space-y-4">
                {sec.paragraphs.map((p, j) => (
                  <p key={j} className="font-body text-navy/75 text-base lg:text-lg leading-relaxed">
                    {p}
                  </p>
                ))}
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function QuoteBlock({ a }: { a: Attorney }) {
  if (!a.quote) return null;
  return (
    <section className="bg-navy text-white py-16 lg:py-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <Quote className="w-10 h-10 text-gold mx-auto mb-6" aria-hidden />
        <blockquote className="font-display italic text-2xl lg:text-3xl leading-tight text-white mb-6">
          &ldquo;{a.quote.text}&rdquo;
        </blockquote>
        {a.quote.attribution ? (
          <cite className="font-body text-gold text-sm uppercase tracking-[0.2em] not-italic">
            — {a.quote.attribution}
          </cite>
        ) : null}
      </div>
    </section>
  );
}

const ICONS: Record<string, typeof GraduationCap> = {
  education: GraduationCap,
  admission: Scale,
  membership: Award,
  language: Globe,
  recognition: Award,
};

const LABELS: Record<string, string> = {
  education: 'Education',
  admission: 'Bar Admissions',
  membership: 'Memberships',
  language: 'Languages',
  recognition: 'Recognition',
};

function CredentialsGrid({ a }: { a: Attorney }) {
  const { ref, visible } = useScrollReveal(0.1);
  const grouped: Record<string, string[]> = {};
  for (const c of a.credentials) {
    if (!grouped[c.type]) grouped[c.type] = [];
    grouped[c.type].push(c.value);
  }
  const order: Array<keyof typeof LABELS> = ['education', 'admission', 'membership', 'language', 'recognition'];
  const groups = order.filter((t) => grouped[t]?.length).map((t) => ({ type: t, items: grouped[t] }));

  return (
    <section ref={ref as React.RefObject<HTMLElement>} className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-12">
          <div className="section-label mb-4">Credentials</div>
          <h2 className="font-display text-navy text-3xl sm:text-4xl lg:text-4xl font-bold leading-tight">
            Education, Bar & <span className="text-gold italic">Affiliations</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {groups.map((g, i) => {
            const Icon = ICONS[g.type] ?? Award;
            return (
              <article
                key={g.type}
                className={`bg-cream border border-navy/10 p-6 lg:p-7 relative transition-all duration-700 ${
                  visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`}
                style={{ transitionDelay: `${i * 70}ms` }}
              >
                <div className="absolute top-0 left-0 w-12 h-0.5 bg-gold" />
                <div className="flex items-center gap-3 mb-4">
                  <Icon className="w-5 h-5 text-gold" />
                  <h3 className="font-display text-navy text-lg font-bold tracking-wide uppercase">
                    {LABELS[g.type]}
                  </h3>
                </div>
                <ul className="space-y-2.5">
                  {g.items.map((v) => (
                    <li key={v} className="font-body text-navy/75 text-sm lg:text-base leading-relaxed">
                      {v}
                    </li>
                  ))}
                </ul>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function PracticeAreasServed({ a }: { a: Attorney }) {
  if (!a.practiceAreas?.length) return null;
  return (
    <section className="py-20 lg:py-28 bg-cream border-t border-navy/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-10 lg:mb-14">
          <div className="section-label mb-4">Practice Areas</div>
          <h2 className="font-display text-navy text-3xl sm:text-4xl font-bold leading-tight">
            How {a.shortName} Can <span className="text-gold italic">Help You</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {a.practiceAreas.map((slug) => {
            const info = practiceAreaLabels[slug];
            const href = `/${slug}/`;
            return (
              <a
                key={slug}
                href={href}
                className="group block p-6 bg-white border border-navy/10 hover:border-gold hover:shadow-lg transition-all duration-300"
              >
                <div className="w-10 h-0.5 bg-gold mb-4 transition-all duration-300 group-hover:w-16" />
                <h3 className="font-display text-navy text-lg lg:text-xl font-bold mb-2 group-hover:text-gold transition-colors">
                  {info?.label ?? slug}
                </h3>
                <p className="font-body text-navy/65 text-sm leading-relaxed mb-4">
                  {info?.blurb ?? 'Related practice area at Morgan Legal Group.'}
                </p>
                <span className="inline-flex items-center gap-1 font-body text-xs uppercase tracking-wider text-gold font-semibold">
                  Learn More
                  <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                </span>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function CtaBand({ a }: { a: Attorney }) {
  return (
    <section className="bg-gold py-16 lg:py-20">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="font-display text-navy text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-5">
          Work With {a.shortName}
        </h2>
        <p className="font-body text-navy/85 text-base lg:text-lg leading-relaxed mb-8 max-w-2xl mx-auto">
          Schedule a free consultation with Morgan Legal Group. {a.shortName} or another senior attorney will personally
          review your matter and outline next steps.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <a href="/appointment/" className="btn-navy">
            <Calendar className="w-4 h-4" />
            Schedule Free Consultation
          </a>
          <a
            href="tel:8885291315"
            className="inline-flex items-center gap-2 border-2 border-navy text-navy font-body font-semibold px-6 py-3 transition-all duration-200 hover:bg-navy hover:text-gold"
          >
            <Phone className="w-4 h-4" />
            888-529-1315
          </a>
        </div>
      </div>
    </section>
  );
}

function useHead(a: Attorney) {
  useEffect(() => {
    const canonical = `${SITE}${a.url}`;
    if (typeof document !== 'undefined') {
      // og:image — портрет самого юриста, а не общесайтовая картинка. До 03.09
      // все 5 484 страницы делили одно изображение: ссылка на карточку юриста
      // разворачивалась в соцсетях чужой обложкой.
      applyPageMeta({
        title: a.metaTitle, description: a.metaDescription, url: canonical, type: 'profile',
        // 🔴 Заглушку-монограмму в og:image НЕ ставим: это SVG, который часть
        // площадок вообще не разворачивает, и она хуже общесайтовой обложки.
        // Трое юристов ждут фотографий — до тех пор у них остаётся общая.
        ...(a.photos[0].fallback.endsWith('.svg') ? {} : { image: `${SITE}${a.photos[0].fallback}` }),
      });
    }
  }, [a]);
}

export default function AttorneyBioTemplate({ attorney }: Props) {
  const schema = buildSchema(attorney);
  useHead(attorney);
  return (
    <div className="min-h-screen bg-cream">
      <SchemaJsonLd schema={schema} />
      <Navbar />
      <main id="attorney-bio">
        <HeroBanner a={attorney} />
        <PhotoGallery a={attorney} />
        <BioSections a={attorney} />
        <QuoteBlock a={attorney} />
        <CredentialsGrid a={attorney} />
        <PracticeAreasServed a={attorney} />
        <CtaBand a={attorney} />
      </main>
      <Footer />
      <FloatingCta />
    </div>
  );
}
